import { markRaw, type Component } from 'vue';
import type { IGridColumnCssRule } from './IGridColumnCssRule';
import { DateComp, DisplayBool, InputBool, InputDateTime, InputNullableInt, ValidatedText } from '@/components';
import type { DateType } from '@/utilities/dateutils';
import InputText from '@/components/Basic/InputText.vue';
import ValidatedInt from '@/components/Basic/ValidatedInt.vue';
import type { IGridAggregate } from './IGridAggregate';
import type { SortDirection } from '@/objects/generated';
import type { SupportedEditorTypes } from '@/objects/Valueparsers/ComponentValueUtils';

interface IGridColumnOptions<TRowData, TCellData = any, TCellRendererParams = any, TCellEditorParams = any, TContextData = any> {
    /**
     * Optional id for the column. This is used to identify the column in the gridApi
     *
     * If not supplied the grid will generate one.
     *
     * See {@link https://www.ag-grid.com/javascript-data-grid/column-definitions/#column-ids"} for the ag-grid documentation
     */
    colId?: string;

    /**
     * The name of the field in the data that this column should display
     * e.g with a data object like {name: 'John', age: 30} if the dataField is 'name' then this column will display 'John'
     */
    dataField: string;

    /**
     * The text to display in the column header. If undefined the dataField will be used
     */
    columnHeaderText?: string;

    /**
     * The width of the column
     */
    width?: number;

    /**
     * The maximum width of the column
     */
    maxWidth?: number;

    /**
     * The minimum width of the column
     */
    minWidth?: number;

    /**
     * Should the column be sortable
     */
    sortable?: boolean;

    /**
     * Should the column be filterable
     */
    filterable?: boolean;

    /**
     * Should the column be editable
     */
    editable?: boolean | ((params: TRowData) => boolean);

    /**
     * Should the column be resizable
     */
    resizable?: boolean;

    /**
     * Should the column be hidden
     */
    hidden?: boolean;

    /**
     * Should the column be pinned to the left or right
     */
    pinned?: 'left' | 'right';

    /**
     * Should the column be movable
     */
    suppressMovable?: boolean;

    /**
     * Should the column be draggable
     */
    draggable?: boolean | ((params: TRowData) => boolean);

    /**
     * Callback to get the actual value of a cell. The actual value can be a complex object, but the cell will only display a single value
     * @param params
     * @returns
     */
    valueGetter?: (params: TRowData) => any;

    /**
     * Callback to get the value to sort on. This is used when the valueGetter returns a complex object, but you want to sort on a single value
     * @param params The row data
     */
    sortValueGetter?: (params: TRowData, sortDirection: SortDirection) => any;

    /**
     * Format the value of a cell before displaying it
     * @param params
     * @returns
     */
    valueFormatterForExport?: (params: TRowData) => any;

    /**
     * Set the value back to the data object when the cell is edited
     * @param params
     * @param value
     * @returns
     */
    valueSetter?: (params: TRowData, value: any) => void;

    /**
     * Set the css class of the cell based on the value of the row
     */
    cellClassRules?: IGridColumnCssRule<TRowData, TCellData>[];

    /**
     * This will be used as WatchSource in a watch in the cell renderer.
     * This can be used in conjunction with the cellClassRules to trigger a refresh of the cell styling
     * Since this is a WatchSource the return value should change any time we want the watch to trigger (and thus refresh the cell)
     * The best thing to do here is to return an object containing all the dependencies that should trigger a refresh.
     * TODO: This should be replaced with a complete override of the AgGrids builtin cell css class stuff so it can just be reactive on the css rules directly.
     * @param row The grid datarow
     * @param cell The cell data
     * @returns This should return a changed value from the last time it was called if we want to refresh the cell
     */
    cellRefreshTrigger?: (row?: TRowData, cell?: TCellData) => any;

    /**
     * The data type of the cell. This is used for sorting and filtering
     */
    cellDataType?: string;

    /**
     * The flex value of the column. This is used to determine how much space the column should take up in the grid
     */
    flex?: number;

    /**
     * The vue component used to render the cell
     */
    cellRenderer: Component<{ value: TCellData } & TCellRendererParams> & SupportedEditorTypes;

    /**
     * The parameters for the cell renderer component
     */
    cellRendererParams?: TCellRendererParams | ((params: TRowData) => TCellRendererParams);

    /**
     * The vue component used to edit the cell
     */
    cellEditor?: Component<{ modelValue: TCellData } & TCellEditorParams> & SupportedEditorTypes;

    /**
     * The parameters for the cell editor component
     */
    cellEditorParams?: TCellEditorParams | ((params: TRowData) => TCellEditorParams);

    /**
     * The vue component used to render the header
     */
    headerComponent?: Component; // TODO: Typer

    /**
     * The parameters for the header component
     */
    headerComponentParams?: any; // TODO: Typer

    /**
     * Overrideable headerValueGetter function.
     */
    headerValueGetter?: (headerText: string) => string;

    /**
     * The direction to sort the column in by default
     */
    sortdirection?: SortDirection;

    /**
     * Get the values for the filter
     */
    filterValues?: (pinnedRowIds?: string[]) => Map<string, any>;

    /**
     * The inferface for the aggregate
     * @returns
     */
    aggregate?: IGridAggregate;

    /**
     * remove the fill handle from the column
     * This means you cant grab and pull the column to fill the cells
     * @default undefined
     */
    suppressFillHandle?: boolean;

    /**
     * If we should group the column
     * @default false
     */
    group?: boolean;

    /**
     * Arbitrary data that can be associated with the column.
     */
    contextData?: TContextData;

    /**
     * Custom classes for the cell
     */
    cellClass?: string;
}
export default class GridColumnOptions<TRowData, TCellData = any, TCellRendererParams = any, TCellEditorParams = any, TContextData = any> {
    public colId?: string;
    public dataField: string;
    public columnHeaderText?: string;
    public width?: number;
    public maxWidth?: number;
    public minWidth?: number;
    public sortable?: boolean;
    public resizable?: boolean;
    public filterable?: boolean;
    public editable?: boolean | ((params: TRowData) => boolean);
    public hidden?: boolean;
    public pinned?: 'left' | 'right';
    public suppressMovable?: boolean;
    public draggable?: boolean | ((params: TRowData) => boolean);
    public cellRenderer: Component<{ value: TCellData } & TCellRendererParams> & SupportedEditorTypes;
    public cellRendererParams?: TCellRendererParams | ((params: TRowData) => TCellRendererParams);
    public cellEditor?: Component<{ modelValue: TCellData } & TCellEditorParams> & SupportedEditorTypes; // TODO: any here should be the type in the cell
    public cellEditorParams?: TCellEditorParams | ((params: TRowData) => TCellEditorParams);
    public headerComponent?: Component; // TODO: Typer
    public headerComponentParams?: any; // TODO: Typer
    public headerValueGetter?: (headerText: string) => string;
    public valueGetter?: (params: TRowData) => any;
    public sortValueGetter?: (params: TRowData, sortDirection: SortDirection) => any;
    public valueFormatterForExport?: (params: TRowData) => any;
    public valueSetter?: (params: TRowData, value: any) => void;
    public cellClassRules?: IGridColumnCssRule<TRowData, TCellData>[];
    public cellRefreshTrigger?: (row?: TRowData, cell?: TCellData) => any;
    public cellDataType?: string;
    public flex?: number;
    public sortdirection?: SortDirection;
    public filterValues?: (pinnedRowIds?: string[]) => Map<string, any>;
    public aggregate?: IGridAggregate;
    public suppressFillHandle?: boolean;
    public group?: boolean;
    public contextData?: TContextData;
    public cellClass?: string;

    /**
     * The id of the column as the grid will report it.
     *
     * If we havent set a colId expliticly the dataField will be used. This is a convenience getter so we dont have to do colId ?? dataField all over
     */
    public get columnId() {
        return this.colId ?? this.dataField;
    }

    public constructor(columnOptions: IGridColumnOptions<TRowData, TCellData, TCellRendererParams, TCellEditorParams, TContextData>) {
        this.colId = columnOptions.colId;
        this.dataField = columnOptions.dataField;
        this.columnHeaderText = columnOptions.columnHeaderText;
        this.width = columnOptions.width;
        this.maxWidth = columnOptions.maxWidth;
        this.minWidth = columnOptions.minWidth;
        this.sortable = columnOptions.sortable;
        this.filterable = columnOptions.filterable;
        this.editable = columnOptions.editable;
        this.resizable = columnOptions.resizable;
        this.suppressMovable = columnOptions.suppressMovable ?? false;
        this.hidden = columnOptions.hidden;
        this.pinned = columnOptions.pinned;
        this.draggable = columnOptions.draggable;
        this.cellRenderer = columnOptions.cellRenderer;
        this.cellRendererParams = columnOptions.cellRendererParams;
        this.cellEditor = columnOptions.cellEditor;
        this.cellEditorParams = columnOptions.cellEditorParams;
        this.headerComponent = columnOptions.headerComponent;
        this.headerComponentParams = columnOptions.headerComponentParams;
        this.headerValueGetter = columnOptions.headerValueGetter;
        this.flex = columnOptions.flex;
        this.valueGetter = columnOptions.valueGetter;
        this.sortValueGetter = columnOptions.sortValueGetter;
        this.valueFormatterForExport = columnOptions.valueFormatterForExport;
        this.valueSetter = columnOptions.valueSetter;
        this.cellClassRules = columnOptions.cellClassRules;
        this.cellRefreshTrigger = columnOptions.cellRefreshTrigger;
        this.cellDataType = columnOptions.cellDataType;
        this.sortdirection = columnOptions.sortdirection;
        this.filterValues = columnOptions.filterValues;
        this.aggregate = columnOptions.aggregate;
        this.suppressFillHandle = columnOptions.suppressFillHandle;
        this.group = columnOptions.group ?? false;
        this.contextData = columnOptions.contextData;
        this.cellClass = columnOptions.cellClass;
    }
}

export type ColumnType = 'string' | 'number' | 'date' | 'datetime' | 'time' | 'boolean';
/**
 * Builder class for creating grid columns
 * This is a helper class to make it easier to create grid columns
 * @example new GridColumnOptionsBuilder('name', 'translation.key', 'DateTime').Build()
 */
export class GridColumnOptionsBuilder<TRowData, TCellData = any> {
    public dataField: string;
    public colId?: string;
    public cellDataType?: string;
    public columnHeaderText?: string;
    public checkBoxSelection?: boolean;
    public maxWidth?: number;
    public minWidth?: number;
    public sortable?: boolean;
    public resizable?: boolean;
    public editable?: boolean;
    public filterable?: boolean;
    public valueFormatterForExport?: (params: TRowData) => any;
    public cellRenderer?: Component<{ value: any }> & SupportedEditorTypes;
    public cellRendererParams?: any;
    public cellEditor?: Component<{ modelValue: any }> & SupportedEditorTypes;
    public cellEditorParams?: any;
    public hidden?: boolean;
    public suppressMovable?: boolean;
    public headerComponent?: Component;
    public headerComponentParams?: any;
    public cellClassRules?: IGridColumnCssRule<TRowData, any>[];
    public aggregate?: IGridAggregate;
    public filterValues?: (pinnedRowIds?: string[]) => Map<string, any>;
    public cellClass?: string;
    public valueGetter?: (params: TRowData) => any;

    public constructor(datafield: string, headerkey: string, colId?: string, columntype?: ColumnType) {
        this.dataField = datafield;
        this.columnHeaderText = headerkey;

        this.colId = colId ?? datafield;

        this.create(columntype);
    }

    /**
     *  Build the column
     * @returns
     */
    public Build = () => {
        return new GridColumnOptions<TRowData>({
            colId: this.colId,
            dataField: this.dataField,
            cellDataType: this.cellDataType,
            columnHeaderText: this.columnHeaderText,
            maxWidth: this.maxWidth,
            minWidth: this.minWidth,
            sortable: this.sortable,
            resizable: this.resizable,
            editable: this.editable,
            filterable: this.filterable ?? false,
            suppressMovable: this.suppressMovable,
            cellRenderer: this.cellRenderer ?? ValidatedText,
            cellRendererParams: this.cellRendererParams,
            cellEditorParams: this.cellEditorParams,
            cellEditor: this.cellEditor,
            hidden: this.hidden,
            cellClassRules: this.cellClassRules,
            headerComponent: this.headerComponent,
            headerComponentParams: this.headerComponentParams,
            aggregate: this.aggregate,
            filterValues: this.filterValues,
            cellClass: this.cellClass,
            valueFormatterForExport: this.valueFormatterForExport,
            valueGetter: this.valueGetter,
        });
    };

    /**
     * Adds a cell renderer to the column
     * @returns
     */
    public CellRenderer(cellRenderer: Component & SupportedEditorTypes, params?: any) {
        this.cellRenderer = markRaw(cellRenderer);
        this.cellRendererParams = params;
        return this;
    }

    /**
     * Adds a value formatter for export to the column
     * @param formatter
     * @returns
     */
    public ExportValueFormatter(formatter: (params: TRowData) => any) {
        this.valueFormatterForExport = formatter;
        return this;
    }

    /**
     * Set the header component for the column
     * This will be used to render the header of the column
     * If no component is provided, the header will use the standard header component
     *
     * @param component
     * @param params
     * @returns
     */
    public Header(component?: Component, params?: any) {
        this.headerComponent = !component ? undefined : markRaw(component);
        this.headerComponentParams = params;
        return this;
    }

    /**
     * Adds a cell editor to the column
     * @returns
     */
    public CellEditor(cellEditor: Component<{ modelValue: TCellData }> & SupportedEditorTypes, params?: any) {
        this.cellEditor = markRaw(cellEditor);
        this.cellEditorParams = params;
        return this;
    }

    /**
     * Sets the column values
     * @param minWidth
     * @param maxWidth
     * @returns
     */
    public Size(minWidth?: number, maxWidth?: number) {
        this.minWidth = minWidth;
        this.maxWidth = maxWidth;

        if (this.minWidth && this.maxWidth && this.minWidth == this.maxWidth) {
            this.resizable = false;
        }
        return this;
    }

    /**
     * Adds a cell rule to the column
     * @param cssRule
     * @returns
     */
    public CssRule = (cssRule: IGridColumnCssRule<TRowData, any>) => {
        if (!this.cellClassRules) {
            this.cellClassRules = [];
        }
        this.cellClassRules.push(cssRule);
        return this;
    };

    /**
     * Add a cell class to the column
     * @param cellClass
     * @returns
     */
    public CellClass = (cellClass: string) => {
        this.cellClass = cellClass;
        return this;
    };

    /**
     * Builds and Insert the column at a specific position.
     * NOTE: This will change the original array and is 1 indexed
     * @param columns
     * @param position as 1 indexed
     * @returns
     */
    public Insert(columns: GridColumnOptions<TRowData>[], position: number) {
        if (position < 1) {
            return this;
        }

        // If the position does not exist add it to the end
        if (!columns[position - 1]) {
            columns.push(this.Build());
        } else {
            columns.splice(position - 1, 0, this.Build());
        }

        return this;
    }

    /**
     * Builds and Insert the column at the end of the array
     * @param columns
     * @returns
     */
    public InsertAtEnd(columns: GridColumnOptions<TRowData>[]) {
        columns.push(this.Build());
        return this;
    }

    /**
     * Add a aggregate function to the column
     * @param aggregate
     * @returns
     */
    public Aggregate(aggregate: IGridAggregate) {
        this.aggregate = aggregate;
        return this;
    }

    /**
     * Create a new column based on type
     * @param datafield the field in the data object
     * @param headerkey Translation key for the header
     * @param columntype
     * @returns
     * @example new GridColumnOptions().Create('datetime')
     */
    private create(columntype?: ColumnType) {
        if (columntype === 'date') {
            this.initDateColumn('DATE');
        } else if (columntype === 'datetime') {
            this.initDateColumn('DATETIME');
        } else if (columntype === 'time') {
            this.initDateColumn('TIME');
        } else if (columntype === 'number') {
            this.initNumberColumn();
        } else if (columntype === 'boolean') {
            this.initBooleanColumn();
        } else {
            this.initStringColumn();
        }
    }

    /**
     * Readonly column
     * @returns
     */
    public ReadOnly() {
        this.editable = false;
        return this;
    }

    /**
     * Set the value getter for the column
     * @param valueGetter
     * @returns
     */
    public ValueGetter(valueGetter: (params: TRowData) => any) {
        this.valueGetter = valueGetter;
        return this;
    }

    /**
     * Enable filter on this column
     * @returns
     */
    public Filterable(filtervalues?: (pinnedRowIds?: string[]) => Map<string, any>) {
        this.filterable = true;
        this.filterValues = filtervalues;
        return this;
    }

    /**
     * Disable sorting on this column
     * @returns
     */
    public SortDisabled() {
        this.sortable = false;
        return this;
    }

    /**
     * We add a column with a selectbox for selecting the row
     */
    public SelectRow() {
        this.checkBoxSelection = true;
        this.editable = false;
        this.sortable = false;
        this.filterable = false;
        this.resizable = false;
        this.suppressMovable = true;
        this.maxWidth = 40;
        this.minWidth = 40;
        this.columnHeaderText = '';
        this.initNumberColumn();
        return this;
    }

    /**
     * Set up the special properties for the string column with default renderer and editor
     */
    private initNumberColumn() {
        this.cellDataType = 'string';
        this.cellRenderer = ValidatedInt;
        this.cellEditor = InputNullableInt;
    }

    /**
     * Set up the special properties for the string column with default renderer and editor
     */
    private initStringColumn() {
        this.cellDataType = 'number';
        this.cellRenderer = ValidatedText;
        this.cellEditor = InputText;
    }

    /**
     * Set up the special properties for the boolean column with default renderer and editor
     */
    private initBooleanColumn() {
        this.cellDataType = 'boolean';
        this.cellRenderer = DisplayBool;
        this.cellEditor = InputBool;
    }

    /**
     * Set up the special properties for the date column with default values
     * @param datetype
     */
    private initDateColumn(datetype: DateType) {
        this.cellDataType = 'string';
        this.cellRenderer = DateComp;
        this.cellRendererParams = {
            format: datetype,
        };
        this.cellEditor = InputDateTime;
        this.cellEditorParams = {
            format: datetype,
        };
    }
}
